import { slugifyUrlSegment } from "./publicProfileUrls";
import { PROFILE_HIGHLIGHT_QUERY_PARAM } from "./domTextHighlight";
import { resolveProfilePictureDisplayUrl } from "./profilePictureUrl";
import type {
  MemberSearchFirm,
  MemberSearchJurisdiction,
  MemberSearchProfessional,
} from "../Services/MemberSearch";
import {
  EMPTY_MEMBER_SEARCH_CRITERIA,
  hasAdvancedMemberSearchCriteria,
  hasMemberSearchCriteria,
  resolveMemberSearchProfessionalTitle,
  resolveMemberSearchServiceType,
  type MemberSearchCriteria,
  type MemberSearchOption,
  type MemberSearchServiceTypeFilter,
  type MemberSearchWithin,
} from "../pages/MemberSearch/memberSearchQuery";
import { ServiceType } from "../Enums/FirmEnum";

export const MEMBER_SEARCH_RESULTS_PATH = "/member-search";
export const ADVANCED_MEMBER_SEARCH_PATH = "/advanced-member-search";

/** React Router path for the full member search results page. */
export const buildMemberSearchPagePath = (query?: string) => {
  const trimmed = String(query ?? "").trim();
  if (!trimmed) {
    return MEMBER_SEARCH_RESULTS_PATH;
  }
  const params = new URLSearchParams({ keyword: trimmed });
  return `${MEMBER_SEARCH_RESULTS_PATH}?${params.toString()}`;
};

export const readMemberSearchKeywordFromSearchParams = (
  searchParams: URLSearchParams
): string =>
  (searchParams.get("keyword") ?? searchParams.get("words") ?? "").trim();

const readOption = (
  searchParams: URLSearchParams,
  idKey: string,
  nameKey: string
): MemberSearchOption | undefined => {
  const id = searchParams.get(idKey)?.trim();
  if (!id) {
    return undefined;
  }
  const related = searchParams
    .get("officeIds")
    ?.split(",")
    .map((value) => value.trim())
    .filter(Boolean);
  const name = searchParams.get(nameKey)?.trim() || "";
  if (idKey === "officeId") {
    return {
      id,
      name,
      ...(related?.length ? { relatedOfficeIds: related } : {}),
    };
  }
  return { id, name };
};

const parseSearchWithin = (value: string | null): MemberSearchWithin | undefined => {
  if (value === "firm" || value === "professional") {
    return value;
  }
  return undefined;
};

const parseServiceType = (
  value: string | null
): MemberSearchServiceTypeFilter | undefined => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) {
    return undefined;
  }
  if (trimmed === "all") {
    return "all";
  }
  if (
    trimmed === ServiceType.Legal ||
    trimmed === ServiceType.Accounting ||
    trimmed === ServiceType["Investment Banking"]
  ) {
    return trimmed;
  }
  return undefined;
};

const readOptionList = (
  searchParams: URLSearchParams,
  idKey: string,
  nameKey: string
): MemberSearchOption[] => {
  const ids = searchParams
    .getAll(idKey)
    .map((value) => value.trim())
    .filter(Boolean)
    .slice(0, 3);
  const names = searchParams.getAll(nameKey);
  return ids.map((id, index) => ({
    id,
    name: names[index]?.trim() || "",
  }));
};

const appendOptions = (
  params: URLSearchParams,
  options: MemberSearchOption[],
  idKey: string,
  nameKey: string
) => {
  for (const option of options.slice(0, 3)) {
    const id = option.id?.trim();
    if (!id) {
      continue;
    }
    params.append(idKey, id);
    params.append(nameKey, option.name?.trim() || "");
  }
};

const setOptionParams = (
  params: URLSearchParams,
  option: MemberSearchOption | undefined,
  idKey: string,
  nameKey: string
) => {
  const id = option?.id?.trim();
  if (!id) {
    return;
  }
  params.set(idKey, id);
  const name = option?.name?.trim();
  if (name) {
    params.set(nameKey, name);
  }
};

export const buildMemberSearchResultsPath = (
  criteria: MemberSearchCriteria
): string => {
  const params = new URLSearchParams();
  const keyword = criteria.keyword.trim();
  if (keyword) {
    params.set("keyword", keyword);
  }
  if (criteria.searchWithin !== "firm" && criteria.lastName.trim()) {
    params.set("lastName", criteria.lastName.trim());
  }
  if (criteria.searchWithin) {
    params.set("searchWithin", criteria.searchWithin);
  }
  const serviceType = resolveMemberSearchServiceType(criteria);
  if (serviceType) {
    params.set("serviceType", serviceType);
  }
  const firmId = criteria.firm?.id?.trim();
  const isFirmMode = criteria.searchWithin === "firm";
  if (criteria.searchWithin !== "professional") {
    if (!(isFirmMode && firmId)) {
      const regions = (criteria.regions?.length
        ? criteria.regions
        : criteria.region?.id
          ? [criteria.region]
          : []
      ).slice(0, 3);
      const locations = (criteria.locations?.length
        ? criteria.locations
        : criteria.location?.id
          ? [criteria.location]
          : []
      ).slice(0, 3);
      if (isFirmMode && regions.length) {
        appendOptions(params, regions, "regionId", "regionName");
      } else if (isFirmMode) {
        appendOptions(params, locations, "countryId", "countryName");
      } else {
        appendOptions(params, regions, "regionId", "regionName");
        appendOptions(params, locations, "countryId", "countryName");
      }
    }
  }
  setOptionParams(params, criteria.firm, "firmId", "firmName");
  setOptionParams(
    params,
    criteria.practiceIndustry,
    "practiceIndustryId",
    "practiceIndustryName"
  );
  setOptionParams(params, criteria.officeLocation, "officeId", "officeName");
  if (criteria.officeLocation?.relatedOfficeIds?.length) {
    params.set(
      "officeIds",
      [...new Set(criteria.officeLocation.relatedOfficeIds.map((id) => String(id).trim()).filter(Boolean))].join(",")
    );
  }
  if (criteria.searchWithin === "professional") {
    setOptionParams(
      params,
      resolveMemberSearchProfessionalTitle(criteria),
      "professionalTitleId",
      "professionalTitleName"
    );
  } else if (criteria.searchWithin !== "firm") {
    setOptionParams(
      params,
      criteria.professionalTitle,
      "professionalTitleId",
      "professionalTitleName"
    );
  }
  const query = params.toString();
  return query
    ? `${MEMBER_SEARCH_RESULTS_PATH}?${query}`
    : MEMBER_SEARCH_RESULTS_PATH;
};

export const readMemberSearchCriteriaFromSearchParams = (
  searchParams: URLSearchParams
): MemberSearchCriteria => {
  const searchWithin = parseSearchWithin(searchParams.get("searchWithin"));
  const keyword = readMemberSearchKeywordFromSearchParams(searchParams);
  const lastName =
    searchWithin === "firm" ? "" : (searchParams.get("lastName") ?? "").trim();
  const firm = readOption(searchParams, "firmId", "firmName");
  let regions =
    searchWithin === "professional"
      ? []
      : readOptionList(searchParams, "regionId", "regionName");
  let locations =
    searchWithin === "professional"
      ? []
      : readOptionList(searchParams, "countryId", "countryName");
  if (searchWithin === "firm" && firm?.id) {
    regions = [];
    locations = [];
  } else if (searchWithin === "firm" && regions.length) {
    locations = [];
  }
  const professionalTitle =
    searchWithin === "firm"
      ? undefined
      : readOption(searchParams, "professionalTitleId", "professionalTitleName");
  const officeLocation = readOption(searchParams, "officeId", "officeName");
  return {
    ...EMPTY_MEMBER_SEARCH_CRITERIA,
    keyword,
    lastName,
    searchWithin,
    serviceType: parseServiceType(searchParams.get("serviceType")),
    firm,
    regions,
    region: regions[0],
    locations,
    location: locations[0],
    practiceIndustry: readOption(
      searchParams,
      "practiceIndustryId",
      "practiceIndustryName"
    ),
    officeLocation,
    professionalTitle,
    professionalTitles:
      searchWithin === "professional" &&
      professionalTitle?.id &&
      professionalTitle.id !== "all"
        ? [professionalTitle]
        : undefined,
  };
};

export const hasMemberSearchUrlCriteria = (
  criteria: MemberSearchCriteria
): boolean =>
  hasMemberSearchCriteria(criteria) || hasAdvancedMemberSearchCriteria(criteria);

export type BuildProfessionalPathOptions = {
  highlight?: string;
};

export const buildProfessionalPath = (
  person: MemberSearchProfessional,
  options?: BuildProfessionalPathOptions,
) => {
  const slug = slugifyUrlSegment(`${person.name ?? ""} ${person.lastName ?? ""}`);
  const id = person.legacyId ?? person.generatedId;
  const base = `/profile2/${slug}/${id}`;
  const highlight = String(options?.highlight ?? "").trim();
  if (!highlight) {
    return base;
  }

  const params = new URLSearchParams();
  params.set(PROFILE_HIGHLIGHT_QUERY_PARAM, highlight);
  return `${base}?${params.toString()}`;
};

export const buildFirmPath = (
  firm: Pick<MemberSearchFirm, "id" | "name" | "legacyId" | "shortId">
) => {
  const slug = slugifyUrlSegment(firm.name);
  const id = firm.legacyId ?? firm.shortId ?? firm.id;
  if (!slug || id == null || id === "") {
    return null;
  }
  return `/member/law-firm/${slug}/${id}`;
};

export const buildJurisdictionPath = (jurisdiction: MemberSearchJurisdiction) =>
  buildFirmPath({
    id: jurisdiction.firmId,
    name: jurisdiction.firmName,
    legacyId: jurisdiction.firmLegacyId,
    shortId: jurisdiction.firmShortId,
  });

export const getPersonPhotoSrc = (person: MemberSearchProfessional) => {
  if (!person.id) {
    return "";
  }
  return resolveProfilePictureDisplayUrl(person.id, person.photo);
};
